import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { Controller, useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';

import { CloseContainer, Popup, PopupContent, PopupTitle } from 'styles/pages/settings.styles';
import { RemoveIcon } from 'styles/components/card.styles';
import { TotemLabel } from 'styles';
import { TotemButton } from 'ui/Button';
import { Checkbox } from 'ui/Checkbox';

import {
    TotemFragmentFragment as Totem,
    useGetTotemReferentCandidatesQuery,
    useUpdateTotemCommunicationMutation,
} from 'data/__generated__';

type CommunicationFormValues = {
    mainUserId: string | null;
    emailPreferences: { orders: boolean; reminders: boolean; productUpdates: boolean };
};

const EMAIL_CATEGORIES: { key: keyof CommunicationFormValues['emailPreferences']; label: string; hint: string }[] = [
    { key: 'orders', label: 'Commandes', hint: 'Confirmations, modifications, première livraison' },
    { key: 'reminders', label: 'Relances', hint: 'Commandes non validées, réactivation' },
    { key: 'productUpdates', label: 'Produits', hint: 'Ruptures, produits remplacés ou retirés' },
];

// Le référent reçoit seul les emails du totem ; sans référent, le totem n'en envoie aucun.
export const CommunicationPopup = ({ totem, close }: { totem: Totem; close: () => void }) => {
    const { data: candidatesData, loading: candidatesLoading } = useGetTotemReferentCandidatesQuery();
    const [updateTotemCommunication, { loading }] = useUpdateTotemCommunicationMutation();
    const [errors, setErrors] = useState<string[]>([]);

    const { control, handleSubmit } = useForm<CommunicationFormValues>({
        defaultValues: {
            mainUserId: totem.mainUser?._id ?? null,
            emailPreferences: {
                orders: totem.emailPreferences.orders,
                reminders: totem.emailPreferences.reminders,
                productUpdates: totem.emailPreferences.productUpdates,
            },
        },
    });

    const referentOptions = useMemo(
        () =>
            (candidatesData?.usersOfOrganization ?? []).flatMap((user) =>
                user
                    ? [
                          {
                              value: user._id,
                              label:
                                  [user.profile.firstname, user.profile.lastname].filter(Boolean).join(' ') ||
                                  user.username,
                          },
                      ]
                    : [],
            ),
        [candidatesData],
    );

    async function onSubmit({ mainUserId, emailPreferences }: CommunicationFormValues) {
        const { data } = await updateTotemCommunication({
            variables: { totemId: totem._id, input: { mainUserId, emailPreferences } },
        });
        const result = data?.updateTotemCommunication;

        if (result?.errors.length) {
            setErrors(result.errors);
            return;
        }

        toast.success('Les préférences de communication du totem sont enregistrées');
        close();
    }

    return (
        <Popup $alignItems="center" $justifyContent="center">
            <PopupContent $paddings={[3, 2, 2, 2]} $spaceBetween $width="560px" onClick={(e) => e.stopPropagation()}>
                <PopupTitle>Référent et emails</PopupTitle>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <TotemLabel>
                        <span>Référent du totem</span>
                        <Hint>
                            Seul destinataire des emails de ce totem, et contact remis au livreur. Sans référent, aucun
                            email n'est envoyé.
                        </Hint>
                        <Controller
                            control={control}
                            name="mainUserId"
                            render={({ field: { value, onChange } }) => (
                                <div data-test="totem-referent">
                                    <Select
                                        isClearable
                                        isLoading={candidatesLoading}
                                        placeholder="Aucun référent"
                                        options={referentOptions}
                                        value={referentOptions.find((option) => option.value === value) ?? null}
                                        onChange={(option) => onChange(option?.value ?? null)}
                                    />
                                </div>
                            )}
                        />
                    </TotemLabel>

                    <Section>
                        <span>Emails envoyés au référent</span>
                        {EMAIL_CATEGORIES.map(({ key, label, hint }) => (
                            <Controller
                                key={key}
                                control={control}
                                name={`emailPreferences.${key}`}
                                render={({ field: { value, onChange } }) => (
                                    <CheckboxLine>
                                        <Checkbox
                                            checked={value}
                                            onChange={(event) => onChange(event.target.checked)}
                                            dataTest={`totem-email-${key}`}
                                        />
                                        <div>
                                            <strong>{label}</strong>
                                            <Hint>{hint}</Hint>
                                        </div>
                                    </CheckboxLine>
                                )}
                            />
                        ))}
                    </Section>

                    {errors.map((error) => (
                        <ErrorLine key={error}>{error}</ErrorLine>
                    ))}

                    <TotemButton type="submit" data-test="totem-communication-submit" disabled={loading}>
                        {loading ? <ClipLoader color="white" size="16px" /> : 'Enregistrer'}
                    </TotemButton>
                </Form>
                <CloseContainer>
                    <RemoveIcon size="25px" onClick={close} />
                </CloseContainer>
            </PopupContent>
        </Popup>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    width: 100%;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-weight: 600;
`;

const CheckboxLine = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-weight: 400;
    cursor: pointer;
`;

const Hint = styled.span`
    display: block;
    font-size: 0.85em;
    font-weight: 400;
    text-transform: none;
    opacity: 0.7;
`;

const ErrorLine = styled.span`
    color: ${({ theme }) => theme.colors.red};
`;
